import { agruparPorMateria, abaixoDaMateria, type CategoriaResumo } from "@noesis/shared";

/**
 * O `<select>` de categoria, agrupado por matéria (RN-37, RN-38).
 *
 * Ele existe porque a mesma lista aparece em quatro telas — caderno, fontes,
 * modelo de prova e dosagem — e as quatro precisam agrupar do mesmo jeito. Antes
 * cada uma indentava com `"— ".repeat(profundidade)`, que dizia a profundidade
 * mas não dizia **onde termina uma matéria e começa a outra**: numa trilha com
 * seis matérias e trinta assuntos, a lista lia como uma coisa só.
 *
 * `<optgroup>` e não indentação: o agrupamento passa a existir para o leitor de
 * tela também, e não só para quem enxerga o recuo.
 *
 * Dentro do grupo o nome perde a matéria — repetir "Banco de dados" em cada
 * linha de um grupo chamado "Banco de dados" é ruído. O que fica é o caminho
 * abaixo dela, que é o que distingue uma linha da outra.
 */
export function SeletorDeCategoria({
  id,
  categorias,
  valor,
  aoMudar,
  rotuloVazio = "Sem categoria",
  ...resto
}: {
  id: string;
  categorias: CategoriaResumo[];
  valor: string | null;
  aoMudar: (id: string | null) => void;
  rotuloVazio?: string;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "id" | "value" | "onChange">) {
  const grupos = agruparPorMateria(categorias, (c) => c.caminho);

  return (
    <select
      id={id}
      className="campo__entrada"
      value={valor ?? ""}
      onChange={(e) => aoMudar(e.target.value || null)}
      {...resto}
    >
      <option value="">{rotuloVazio}</option>
      {grupos.map((g) => (
        <optgroup key={g.materia ?? "(sem matéria)"} label={g.materia ?? "(sem matéria)"}>
          {g.itens.map((c) => {
            const dentro = abaixoDaMateria(c.caminho);
            return (
              <option key={c.id} value={c.id}>
                {dentro.length === 0 ? c.nome : `${"— ".repeat(dentro.length - 1)}${dentro.join(" › ")}`}
              </option>
            );
          })}
        </optgroup>
      ))}
    </select>
  );
}
